import React, { useState } from 'react';
import { usePIM } from '../../context/PIMContext';

import { Trash, Plus, Settings, FileText, Download, ExternalLink, Edit, Pen, RefreshCw } from 'lucide-react';
import { GhostButton } from '../ui/GhostButton';
import { Badge } from '../ui/Badge';
import { DocumentResourceModal } from '../modals/DocumentResourceModal';
import { LinkDocumentModal } from '../modals/LinkDocumentModal';
import { ReindexModal } from '../modals/ReindexModal';
import { useToast } from '../../context/ToastContext';
import { ResourceTypeManagementModal } from '../modals/ResourceTypeManagementModal';
import * as Icons from 'lucide-react';

import { useSortableData } from '../../hooks/useSortableData';
import { SortableHeader } from '../ui/SortableHeader';
import { RagStatusBadge } from '../ui/RagStatusBadge';

export const DocumentsTab = () => {
    const { toast } = useToast();
    const {
        documentResources,
        deleteDocumentResource,
        updateDocumentResource
    } = usePIM();


    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
    const [isManageModalOpen, setIsManageModalOpen] = useState(false);
    const [editingResource, setEditingResource] = useState(null);
    const [reindexModalOpen, setReindexModalOpen] = useState(false);
    const [selectedDocForReindex, setSelectedDocForReindex] = useState(null);

    // Sorting
    const { items: sortedResources, requestSort, sortConfig } = useSortableData(documentResources, { key: 'type_sort_order', direction: 'ascending' });

    // Grouping
    // const [collapsedGroups, setCollapsedGroups] = useState({});

    // const toggleGroup = (type) => {
    //     setCollapsedGroups(prev => ({
    //         ...prev,
    //         [type]: !prev[type]
    //     }));
    // };



    const getIcon = (iconName) => {
        const Icon = Icons[iconName] || Icons.File;
        return <Icon size={16} />;
    };

    const handleEdit = (resource) => {
        setEditingResource(resource);
        setIsAddModalOpen(true);
    };

    const handleCloseAddModal = () => {
        setIsAddModalOpen(false);
        setEditingResource(null);
    };

    const handleVisibilityChange = async (res, newVisibility) => {
        try {
            await updateDocumentResource(res.id, {
                ...res,
                release_date: res.release_date ? res.release_date.split('T')[0] : null,
                visibility: newVisibility
            });
            toast.success("Visibility updated");
        } catch (err) {
            toast.error("Failed to update visibility");
        }
    };

    return (
        <div className="h-full flex flex-col">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-xl font-bold text-white">Documents</h2>
                    <p className="text-xs text-stone-500 mt-1">Manage product documentation, manuals, and certificates.</p>
                </div>
                <div className="flex gap-3">
                    <GhostButton onClick={() => setIsLinkModalOpen(true)}><Icons.Link size={16} /> Add Link</GhostButton>
                    <GhostButton onClick={() => setIsAddModalOpen(true)}><Plus size={16} /> New Document</GhostButton>
                    <GhostButton onClick={() => setIsManageModalOpen(true)}><Settings size={16} /> Manage Types</GhostButton>
                </div>
            </div>

            <div className="bg-stone-900/50 border border-stone-700 rounded-xl overflow-hidden shadow-xl flex-1 flex flex-col">
                <div className="overflow-y-auto flex-1">
                    <table className="w-full text-left border-collapse">
                        <thead className="sticky top-0 z-10">
                            <tr>
                                <th className="bg-stone-950 text-stone-500 text-xs uppercase font-semibold px-4 py-3 border-b border-stone-800 w-10"></th>
                                <SortableHeader label="Name" sortKey="name" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Type" sortKey="type_sort_order" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Version" sortKey="version" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Date" sortKey="release_date" currentSort={sortConfig} onSort={requestSort} />
                                <th className="bg-stone-950 text-stone-500 text-xs uppercase font-semibold px-4 py-3 border-b border-stone-800">RAG</th>
                                <SortableHeader label="Visibility" sortKey="visibility" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Path / Link" sortKey="file_path" currentSort={sortConfig} onSort={requestSort} />
                                <th className="bg-stone-950 text-stone-500 text-xs uppercase font-semibold px-4 py-3 border-b border-stone-800 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedResources.length === 0 ? (
                                <tr>
                                     <td colSpan="9" className="px-4 py-8 text-center text-stone-500 text-sm">
                                        No documents found for this charge.
                                    </td>
                                </tr>
                            ) : (
                                sortedResources.map(res => (
                                    <tr key={res.id} className="hover:bg-stone-800/30 transition-colors border-b border-stone-800 last:border-0">
                                        <td className="px-4 py-0.5 text-stone-400">
                                            {getIcon(res.type_icon)}
                                        </td>
                                        <td className="px-4 py-0.5 text-sm text-stone-200 font-medium">{res.name}</td>
                                        <td className="px-4 py-0.5 text-sm text-stone-400">
                                            <Badge color={res.type_color || 'purple'} size="sm">{res.type}</Badge>
                                        </td>
                                        <td className="px-4 py-0.5 text-sm text-stone-400">{res.version}</td>
                                        <td className="px-4 py-0.5 text-sm text-stone-400">
                                            {res.release_date ? new Date(res.release_date).toLocaleDateString() : '-'}
                                        </td>
                                        <td className="px-4 py-0.5">
                                            <RagStatusBadge documentId={res.id} />
                                        </td>
                                        <td className="px-4 py-0.5">
                                            <select
                                                value={res.visibility || 'sensitive'}
                                                onChange={(e) => handleVisibilityChange(res, e.target.value)}
                                                className={`text-[10px] font-bold border rounded px-1 py-0.5 bg-stone-950 outline-none cursor-pointer focus:border-blue-500 transition-colors ${
                                                    (res.visibility === 'public') ? 'text-green-400 border-green-800/50' : 
                                                    (res.visibility === 'blocked') ? 'text-red-400 border-red-800/50' :
                                                    'text-amber-500 border-amber-800/50'
                                                }`}
                                            >
                                                <option value="public" className="text-green-400 bg-stone-900">PUBLIC</option>
                                                <option value="sensitive" className="text-amber-500 bg-stone-900">SENSITIVE</option>
                                                <option value="blocked" className="text-red-400 bg-stone-900">BLOCKED</option>
                                            </select>
                                        </td>
                                        <td className="px-4 py-0.5 text-sm text-stone-400 truncate max-w-[100px]" title={res.file_path}>
                                            {res.mime_type === 'link' ? (
                                                <a href={res.file_path} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-400 hover:underline truncate">
                                                    <ExternalLink size={12} className="flex-shrink-0" /> <span className="truncate">{res.file_path}</span>
                                                </a>
                                            ) : (
                                                <span className="flex items-center gap-1 truncate">
                                                    <FileText size={12} className="flex-shrink-0" /> <span className="truncate">{res.file_path}</span>
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-0.5 text-right">
                                            <div className="flex justify-end gap-2">
                                                {res.mime_type !== 'link' && (
                                                    <>
                                                        <button
                                                            onClick={() => {
                                                                setSelectedDocForReindex(res);
                                                                setReindexModalOpen(true);
                                                            }}
                                                            className="w-7 h-7 rounded border border-stone-600 text-stone-400 hover:text-purple-400 hover:border-purple-900/50 hover:bg-purple-900/10 flex items-center justify-center transition"
                                                            title="Re-Index (RAG)"
                                                        >
                                                            <RefreshCw size={14} />
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                // Use direct backend download route
                                                                window.location.href = `/api/charge-documents/${res.id}/download`;
                                                            }}
                                                            className="w-7 h-7 rounded border border-stone-600 text-stone-400 hover:text-blue-400 hover:border-blue-900/50 hover:bg-blue-900/10 flex items-center justify-center transition"
                                                            title="Download"
                                                        >
                                                            <Download size={14} />
                                                        </button>
                                                    </>
                                                )}
                                                {res.file_path && res.mime_type !== 'link' && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            const webdavBase = `${window.location.origin}/webdav`;
                                                            const fullPath = res.file_path.startsWith('/') ? res.file_path : `/${res.file_path}`;
                                                            const webdavUrl = `${webdavBase}${fullPath}`;
                                                            const lowerPath = res.file_path.toLowerCase();

                                                            let editUrl = webdavUrl;
                                                            let isOffice = false;
                                                            if (lowerPath.endsWith('.docx') || lowerPath.endsWith('.doc')) {
                                                                editUrl = `ms-word:ofe|u|${webdavUrl}`;
                                                                isOffice = true;
                                                            } else if (lowerPath.endsWith('.xlsx') || lowerPath.endsWith('.xls')) {
                                                                editUrl = `ms-excel:ofe|u|${webdavUrl}`;
                                                                isOffice = true;
                                                            } else if (lowerPath.endsWith('.pptx') || lowerPath.endsWith('.ppt')) {
                                                                editUrl = `ms-powerpoint:ofe|u|${webdavUrl}`;
                                                                isOffice = true;
                                                            }

                                                            if (isOffice) {
                                                                toast.info("Opening document in Microsoft Office...");
                                                                window.location.href = editUrl;
                                                            } else {
                                                                // Custom protocol for generic files (PDFs, Illustrator, etc.)
                                                                // Requires PimEditLocalLauncher installed on the client machine
                                                                if (window.confirm(`Möchtest du diese Datei lokal über das 'pim-edit' Protokoll in deinem Desktop-Programm ({res.type}) öffnen?\n\nAchtung: Das erfordert, dass das pim-launcher Skript auf deinem Windows-PC installiert ist.`)) {
                                                                    // Inject Basic Auth credentials into the URL for the PowerShell script to read
                                                                    const hostWithoutProtocol = webdavBase.replace('https://', '').replace('http://', '');
                                                                    const protocol = webdavBase.startsWith('https') ? 'https://' : 'http://';
                                                                    // Note: hardcoded password per user request for workflow convenience
                                                                    const authUrl = `${protocol}ulrix:MMindthe131!!@${hostWithoutProtocol}${fullPath}`;
                                                                    editUrl = `pim-edit://${encodeURIComponent(authUrl)}`;
                                                                    toast.info(`Opening ${res.name} locally...`);
                                                                    window.location.href = editUrl;
                                                                }
                                                            }
                                                        }}
                                                        className="w-7 h-7 rounded border border-stone-600 text-stone-400 hover:text-green-400 hover:border-green-900/50 hover:bg-green-900/10 flex items-center justify-center transition"
                                                        title={`Direct Edit via WebDAV`}
                                                    >
                                                        <Pen size={14} />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => handleEdit(res)}
                                                    className="w-7 h-7 rounded border border-stone-600 text-stone-400 hover:text-yellow-400 hover:border-yellow-900/50 hover:bg-yellow-900/10 flex items-center justify-center transition"
                                                    title="Edit"
                                                >
                                                    <Edit size={14} />
                                                </button>
                                                <button
                                                    onClick={() => deleteDocumentResource(res.id)}
                                                    className="w-7 h-7 rounded border border-stone-600 text-stone-400 hover:text-red-400 hover:border-red-900/50 hover:bg-red-900/10 flex items-center justify-center transition"
                                                    title="Delete"
                                                >
                                                    <Trash size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <LinkDocumentModal
                isOpen={isLinkModalOpen}
                onClose={() => setIsLinkModalOpen(false)}
            />

            <DocumentResourceModal
                isOpen={isAddModalOpen}
                onClose={handleCloseAddModal}
                resourceToEdit={editingResource}
            />

            <ReindexModal
                isOpen={reindexModalOpen}
                onClose={() => setReindexModalOpen(false)}
                document={selectedDocForReindex}
                onReindex={(jobId) => {
                    console.log("Re-indexing started, job:", jobId);
                    // Optionally refresh the list or trigger a toast
                    // The RagStatusBadge should auto-update on its next poll or we can force it?
                    // For now, relied on polling.
                }}
            />

            <ResourceTypeManagementModal
                isOpen={isManageModalOpen}
                onClose={() => setIsManageModalOpen(false)}
                defaultCategory="Document"
            />
        </div >
    );
};
